import {MvsCrudService} from "../../../../../../core/shared/service/crud/mvs-crud.service";
import {Component, EventEmitter, OnInit, Output, SimpleChanges} from '@angular/core';
import {LogicCheckSyntaxDto} from "../../../../model/dto/entity/logic-check-syntax.dto";
import {LogicDto} from "../../../../model/dto/entity/logic.dto";
import {LogicCheckSyntaxResponseDto} from "../../../../model/dto/entity/logic-check-syntax-response.dto";
import {ActivatedRoute} from "@angular/router";
import {ConfirmationService, MenuItem} from "primeng/api";
import {LogicService} from "../../../../service/api/logic.service";
import {ObjectBaseModeComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base-mode.component";
import {MvsCoreService} from "../../../../../../core/shared/service/mvs-core.service";
import {MvsMessageService} from "../../../../../../core/shared/service/message/mvs-message.service";
import {LogicLanguage} from "../../../../../../core/shared/components/code-mirror-editor/code-mirror-editor.component";
import {WidgetHelperButton} from "../../../../../../core/shared/basic/widget-helper-button";
import {ObserverService} from "../../../../../../core/shared/object/service/observer.service";
// TM-1000001726: expose the LogicLanguage enum to templates so the format-editor
// branch can be selected conditionally without string comparisons.
import {LogicLanguageEnum} from "../../../../enum/logic-language.enum";

@Component({
    selector: 'mvs-lg-logic-object-component-base',
    template: '',
    standalone: false
})
export class LgLogicObjectBaseComponent extends ObjectBaseModeComponent implements OnInit {


    logicExportService: MvsCrudService;
    logicImportService: MvsCrudService;
    dto: LogicDto;

    script: string;
    language: string;
    currentView: string = 'logic';
    activeTab: number = 0;

    syntaxResponse: LogicCheckSyntaxResponseDto;
    widgetButtons: WidgetHelperButton[];

    @Output() onBreadcrumbItems: EventEmitter<MenuItem[]> = new EventEmitter();

    /** TM-1000001726: expose enum for template comparisons (format branch). */
    LogicLanguageEnum = LogicLanguageEnum;

    /**
     * TM-1000001726: numeric ordinal of LogicLanguageEnum.format mirroring the
     * backend `LogicLanguage.format` value. Used in templates because
     * {@code LogicDto.logicLanguage} is typed against the {@code LogicLanguage}
     * enum (CodeMirror) and the two TS-enums do not type-overlap even though
     * they share ordinals at runtime.
     */
    readonly logicLanguageFormatOrdinal: number = LogicLanguageEnum.format as unknown as number;

    /**
     * TM-1000001726 Phase 2: numeric ordinal of LogicLanguageEnum.module_function.
     * Used in templates to switch to the structured Module-Function editor.
     */
    readonly logicLanguageModuleFunctionOrdinal: number =
        LogicLanguageEnum.module_function as unknown as number;

    /**
     * TM-1000001726 Phase 3: numeric ordinal of LogicLanguageEnum.crud.
     * Used in templates to switch to the structured CRUD-Script-Editor.
     */
    readonly logicLanguageCrudOrdinal: number =
        LogicLanguageEnum.crud as unknown as number;

    /**
     * Numeric ordinal of LogicLanguageEnum.mapping (11) — the language whose entire configuration is
     * the typed FK {@code Logic.mappingTable} and which therefore has no script at all.
     *
     * Same ordinal detour as the three languages above, for the same documented reason: `logicLanguage`
     * is typed against the CodeMirror `LogicLanguage` enum, which does not type-overlap with the
     * backend mirror even though the two share ordinals at runtime.
     */
    readonly logicLanguageMappingOrdinal: number =
        LogicLanguageEnum.mapping as unknown as number;

    /**
     * TM-1000001726: template-friendly check whether the current logic uses
     * the structured format-function language.
     */
    isFormatLanguage(): boolean {
        return (this.dto?.logicLanguage as unknown as number) === this.logicLanguageFormatOrdinal;
    }

    /**
     * TM-1000001726 Phase 2: template-friendly check whether the current logic
     * uses the structured module-function language.
     */
    isModuleFunctionLanguage(): boolean {
        return (this.dto?.logicLanguage as unknown as number) === this.logicLanguageModuleFunctionOrdinal;
    }

    /**
     * TM-1000001726 Phase 3: template-friendly check whether the current logic
     * uses the structured CRUD-Operation language.
     */
    isCrudLanguage(): boolean {
        return (this.dto?.logicLanguage as unknown as number) === this.logicLanguageCrudOrdinal;
    }

    /**
     * Template-friendly check whether the current logic resolves against a mapping table.
     *
     * This is a pure type check — the language IS the marker, there is no script to scan.
     */
    isMappingLanguage(): boolean {
        return (this.dto?.logicLanguage as unknown as number) === this.logicLanguageMappingOrdinal;
    }

    /** TM-1000001726: variable names imported by the logic — fed into the format editor. */
    importVariableNames: string[] = [];

    constructor(protected route: ActivatedRoute,
                protected coreService: MvsCoreService,
                protected confirmationService: ConfirmationService,
                protected messageService: MvsMessageService,
                protected observerService: ObserverService,
                protected logicService: LogicService
    ) {
        super(route);
    }

    ngOnInit() {
        // initialize navigation items
        this.loadSideBarMenuItems();

        // Must run BEFORE super.ngOnInit(): the base class resolves the active section from the
        // navItem / sideNavItem query parameters against this list, and its route.queryParams
        // subscription fires SYNCHRONOUSLY (it documents that itself). An item added afterwards is
        // invisible to that resolution, so a deep link to ?navItem=quickTest would silently fall back
        // to the default tab. `mappings` still cannot be decided here — the language is only known
        // once the DTO arrives — which is why onObjectChanged() re-resolves.
        this.applyConditionalNavigationItems();

        super.ngOnInit();



        this.logicExportService = this.coreService.getCrudService("lg.LogicExport");
        this.logicImportService = this.coreService.getCrudService("lg.LogicImport");
        if (this.dto) {

            this.onObjectChanged();
        } else {
            this.loadData();
        }

        this.initialized = true;
    }

    loadData() {
        this.logicService.get(this.objectIdentifier.objectId).subscribe((res: LogicDto) => {
            this.dto = res;
            this.onObjectChanged();
        })
    }


    /**
     * load and initialize sidebar navigations
     */
    loadSideBarMenuItems() {

    }


    onObjectChanged() {
        this.script = this.dto.script;
        // Resolved against the 12-value backend mirror, not the editor's own enum: the value is a
        // backend ordinal and the two enums diverge from position 2 on.
        this.language = LogicLanguageEnum[this.dto.logicLanguage as unknown as number];

        const item: MenuItem[] = [{
            icon: this.dto['image'],
            label: this.dto['name'],
            id: this.dto.id as unknown as string,
        }];

        this.onBreadcrumbItems.emit(item);

        // TM-1000001726: pre-load LogicImport variable names so the format editor
        // can offer a variable picker for `$$varName` references.
        this.loadImportVariableNames();

        // The language is only known once the DTO is here, so the mapping-only item can only be
        // decided now. Re-emitting is what makes it appear in the host's navigation.
        this.applyConditionalNavigationItems();
        this.handleNavigationItemsChange();
        this.resolveActiveNavigationItemFromQueryParam();
    }

    /**
     * Re-resolves the active section from the URL after the DTO-dependent items were added.
     *
     * The base class resolves `navItem` / `sideNavItem` once, during its own `ngOnInit`, and never
     * again. `mappings` does not exist at that moment — it depends on the language, which arrives with
     * the DTO — so a deep link to `?navItem=mappings` would land on the default tab and stay there.
     * Only a section that is actually in the list is applied, so this can never select a tab the
     * component does not render.
     */
    protected resolveActiveNavigationItemFromQueryParam(): void {
        const params = this.route?.snapshot?.queryParams;

        if (!params || !this.navigationItems?.length) {
            return;
        }

        const navigationAction: string = params['navItem'] && decodeURIComponent(params['navItem']);
        const sideNavigationAction: string = params['sideNavItem'] && decodeURIComponent(params['sideNavItem']);

        const matchedItem = this.navigationItems.find(item => item.action === navigationAction);
        const sideMatchedItem = this.navigationItems.find(item => item.action === sideNavigationAction);

        // Same precedence as ObjectBaseModeComponent: a side view reads sideNavItem, everything else
        // prefers navItem and falls back to sideNavItem.
        const isSideMode: boolean = this.uiMode === 'side' || this.uiMode === 'mini-side';
        const navItem = isSideMode ? sideMatchedItem : (matchedItem ?? sideMatchedItem);

        if (!navItem || navItem === this.activeNavigationItem) {
            return;
        }

        this.activeNavigationItem = navItem;

        // Deferred for the same reason the base class defers: emitting inside the running change
        // detection cycle rewrites the parent's binding and raises NG0100.
        queueMicrotask(() => {
            this.activeNavigationItemChange.emit(navItem);
            this.onNavigationItemChange(navItem);
        });
    }

    /**
     * Appends the navigation items that depend on the loaded logic.
     *
     * Idempotent so it can run both before and after the DTO arrives:
     * - **Schnelltest** is added for every language — every logic block can be executed, and the
     *   endpoint behind it had no frontend at all until now.
     * - **Mappings** is added only for {@link isMappingLanguage}, and is inserted BEFORE the quick
     *   test so the order stays the same regardless of which call added which item.
     *
     * The actions are deliberately not called `testen` or `TestCases`: both names are already taken by
     * the persisted test cases in the side and full views, and reusing one would silently swap the two
     * surfaces.
     */
    protected applyConditionalNavigationItems(): void {
        if (!this.navigationItems) {
            return;
        }

        if (!this.navigationItems.some(item => item.action === 'quickTest')) {
            this.navigationItems.push({
                label: 'Schnelltest',
                action: 'quickTest',
                tooltip: 'Logik einmal ausführen und das Ergebnis ansehen',
                icon: 'fa-regular fa-gauge-high',
                toggleable: false,
                default: false
            });
        }

        if (this.isMappingLanguage() && !this.navigationItems.some(item => item.action === 'mappings')) {
            const quickTestIndex: number = this.navigationItems.findIndex(item => item.action === 'quickTest');
            const mappingsItem = {
                label: 'Mappings',
                action: 'mappings',
                tooltip: 'Zugeordnete Mapping-Tabelle mit Einträgen und Partner-Overrides',
                icon: 'fa-regular fa-arrow-right-arrow-left',
                toggleable: false,
                default: false
            };

            if (quickTestIndex >= 0) {
                this.navigationItems.splice(quickTestIndex, 0, mappingsItem);
            } else {
                this.navigationItems.push(mappingsItem);
            }
        }
    }

    /**
     * Handler for the mapping-table picker: keeps the local DTO in sync after the picker has persisted
     * the FK, and re-evaluates the navigation in case the logic only just became a mapping logic.
     *
     * @param mappingTableId the newly assigned mapping table, or null when it was cleared
     */
    onMappingTableChanged(mappingTableId: number): void {
        if (this.dto) {
            this.dto.mappingTableDtoId = mappingTableId;
        }

        this.applyConditionalNavigationItems();
        this.handleNavigationItemsChange();
    }

    /**
     * TM-1000001726: loads the import-variable names for the current logic and
     * exposes them to the format-editor. Errors are swallowed because this is a
     * best-effort UX-enhancement, not a correctness requirement.
     *
     * The attribute is the RELATION name `logic`. It used to read `logicDto.id`, which is not a JPA
     * attribute: `FilterService.toPredicate` resolves the field straight against the root
     * (`root.get(field)`, `core/filter/FilterService.java:281`) and throws on a dotted path. Because
     * the error branch here is silent by design, the variable picker of the format editor simply
     * stayed empty and never said why.
     */
    protected loadImportVariableNames() {
        if (!this.logicImportService || !this.dto?.id) {
            return;
        }
        this.logicImportService.listByAttribute('logic', this.dto.id).subscribe({
            next: (res: any) => {
                const items = res?.entries ?? [];
                this.importVariableNames = items
                    .map((it: any) => it?.variableName)
                    .filter((name: any) => typeof name === 'string' && name.length > 0);
            },
            error: () => {
                this.importVariableNames = [];
            }
        });
    }

    /**
     * TM-1000001726: handler bound to the format-script-editor; keeps the local
     * {@link script} in sync with the structured editor's serialized JSON.
     */
    onFormatScriptChange(scriptJson: string) {
        this.script = scriptJson;
    }

    /**
     * TM-1000001726 Phase 2: handler bound to the module-function-script-editor;
     * keeps the local {@link script} in sync with the structured editor's
     * serialized JSON envelope.
     */
    onModuleFunctionScriptChange(scriptJson: string) {
        this.script = scriptJson;
    }

    /**
     * TM-1000001726 Phase 3: handler bound to the crud-script-editor; keeps
     * the local {@link script} in sync with the structured editor's serialized
     * JSON descriptor.
     */
    onCrudScriptChange(scriptJson: string) {
        this.script = scriptJson;
    }


    checkSyntax() {
        const codeSyntaxDto: LogicCheckSyntaxDto = new LogicCheckSyntaxDto();

        codeSyntaxDto.logicId = this.objectIdentifier.objectId;
        codeSyntaxDto.logicLanguage = this.dto.logicLanguage;
        codeSyntaxDto.code = this.script;

        this.logicService.checkCodeSyntax(codeSyntaxDto).subscribe(checkSyntax => {
            this.syntaxResponse = checkSyntax;
        })
    }

    updateScript() {

        const logicDto: LogicDto = new LogicDto();
        logicDto.id = this.dto.id;
        logicDto.script = this.script;
        logicDto.logicLanguage = this.dto.logicLanguage;

        this.logicService.update(logicDto).subscribe((res: LogicDto) => {
            this.dto = res;
            this.messageService.showSuccessMessage('Update', 'Logic has benn updated.');
        });

    }

    onContentChange(content: string) {
        this.script = content;
    }

    handleLogicLanguageChange(event: LogicLanguage) {
        this.dto.logicLanguage = event;
    }

    setView(view: string) {
        this.currentView = view;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }

    }

    handleNavigationItemsChange(): void {
        this.onNavigationItems.emit(this.navigationItems);
    }


}
